import { useAuth0 } from '@auth0/auth0-react';
import React from 'react';
import { ReactComponent as DreamLogoIcon } from '../icons/dream-logo-icon.svg';
import { ReactComponent as DreamLogoWordmark } from '../icons/dream-logo-wordmark.svg';
import './auth-error-page.scss';

interface AuthErrorPageProps {
  /** Overrides the `error_description` query parameter. */
  description?: string;
}

/**
 * Shown when Auth0 redirects back with `?error=…` (e.g. the post-login Action
 * denied a Google/Microsoft login that isn't linked to a Dream account), or
 * when the backend refuses to bootstrap an account for the signed-in user.
 * The button logs out of Auth0 before returning to the sign-in page, so a
 * retry does not silently re-use the live Auth0/Google session that was just
 * denied.
 */
const AuthErrorPage: React.FC<AuthErrorPageProps> = ({ description }) => {
  const { logout } = useAuth0();
  // Read straight from the URL: auth0-spa-js reports "Invalid state" on reload
  // and falls back to the bare error code when there is no description.
  const message =
    description ??
    new URLSearchParams(window.location.search).get('error_description');

  const handleTryAgain = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <div className='auth-error-page'>
      <div className='auth-error-content'>
        <div className='auth-error-logo'>
          <DreamLogoIcon className='auth-error-logo-icon' />
          <DreamLogoWordmark className='auth-error-logo-wordmark' />
        </div>
        <div className='auth-error-text'>
          <h1 className='auth-error-title'>We couldn&rsquo;t sign you in</h1>
          <p className='auth-error-message'>
            {message || 'Something went wrong during sign-in.'}
          </p>
          <p className='auth-error-hint'>
            Sign in with the phone number you use with Dream on WhatsApp, or
            with a Google or Microsoft account you connected to Dream. New to
            Dream? Start a conversation with your secretary on WhatsApp first.
          </p>
        </div>
        <button
          type='button'
          className='auth-error-button'
          onClick={handleTryAgain}
        >
          Back to sign in
        </button>
      </div>
    </div>
  );
};

export default AuthErrorPage;
